import { useState, useEffect, useCallback, useMemo } from "react";
import useGlobalDataRefresh from "hooks/useGlobalDataRefresh";
import { useNavigate, useLocation } from "react-router-dom";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Popover from "@mui/material/Popover";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftSelect from "components/SoftSelect";
import SoftBadge from "components/SoftBadge";
import SoftPagination from "components/SoftPagination";

import Table from "examples/Tables/Table";
import MissionForm from "./Users/components/forms/MissionForm";
import { useAuth } from "context/AuthContext";
import { guestMissions, guestNonprofits } from "data/guestDemoData";
import {
  getMissions,
  getMissionById,
  getNonProfits,
  saveMission,
} from "./Users/services/graphqlService";
import { saveMissionWithDocuments } from "utils/missionDocuments";
import {
  canEditMissionForProfile,
  isInactiveMissionStatus,
  isMissionOwner,
} from "./missionOwnerUtils";
import {
  formatMissionEndpoint,
  normalizeMissionRecord,
  parseStateFromMissionLocation,
  getMissionVisibility,
} from "./missionLocationUtils";
import { applyMissionDerivedStatus } from "./missionSourcingStatus";

function normalizeMissionsList(data) {
  if (!data) return [];
  let list = [];
  if (Array.isArray(data)) list = data;
  else if (Array.isArray(data?.Missions)) list = data.Missions;
  else if (Array.isArray(data?.MissionsList)) list = data.MissionsList;
  else if (Array.isArray(data?.MissionList)) list = data.MissionList;
  else if (Array.isArray(data?.missions)) list = data.missions;
  else if (Array.isArray(data?.items)) list = data.items;
  else if (Array.isArray(data?.data)) list = data.data;
  return list.map((mission) =>
    mission && typeof mission === "object"
      ? applyMissionDerivedStatus(normalizeMissionRecord(mission))
      : mission,
  );
}

function normalizeNonprofitsList(data) {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.NonProfitsList)) return data.NonProfitsList;
  if (Array.isArray(data?.nonprofits)) return data.nonprofits;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.data)) return data.data;
  return [];
}

function getNonprofitId(np) {
  return String(np?.nonprofitId ?? np?.id ?? "").trim();
}

function getNonprofitLabel(np) {
  return np?.name || `Organization ${getNonprofitId(np) || "—"}`;
}

const FILTER_CATEGORY_OPTIONS = [
  { value: "", label: "All categories" },
  { value: "Medical", label: "Medical" },
  { value: "Animal Rescue", label: "Animal Rescue" },
  { value: "Disaster Relief", label: "Disaster Relief" },
  { value: "Veteran Support", label: "Veteran Support" },
  { value: "Conservation", label: "Conservation" },
  { value: "Aviation Experience", label: "Aviation Experience" },
  { value: "other", label: "Others" },
];

const FILTER_NEED_OPTIONS = [
  { value: "", label: "All" },
  { value: "cargo", label: "Looking for cargo" },
  { value: "pilot", label: "Looking for pilots" },
  { value: "both", label: "Looking for both" },
];

const FILTER_FLIGHT_HOUR_OPTIONS = [
  { value: "", label: "All" },
  { value: "None", label: "None" },
  { value: "100", label: "100" },
  { value: "150", label: "150" },
  { value: "200", label: "200" },
  { value: "250", label: "250" },
  { value: "500", label: "500" },
  { value: "1000", label: "1000" },
  { value: "1500+", label: "1500+" },
];

const FILTER_RATING_OPTIONS = [
  { value: "", label: "All" },
  { value: "commercialRating", label: "Commercial Rating" },
  { value: "instrumentFlightRating", label: "Instrument Flight Rating" },
  { value: "privatePilotsLicense", label: "Private Pilot's License" },
];

const FILTER_VISIBILITY_OPTIONS = [
  { value: "", label: "All" },
  { value: "Public", label: "Public" },
  { value: "Internal", label: "Internal" },
];

function isDraftMissionStatus(mission) {
  const status = String(mission?.status || "")
    .trim()
    .toLowerCase();
  return status === "draft" || status.includes("draft");
}

function isNonprofitEntityType(entityType) {
  const value = String(entityType || "")
    .trim()
    .toLowerCase();
  return value === "nonprofit" || value === "nonprofits" || value === "org";
}

/** Missions that belong to this nonprofit user: owned by them, or tagged to their org. */
function isNonprofitMyMission(mission, { entityId, profileId } = {}) {
  if (isMissionOwner(mission, profileId)) return true;
  const id = String(entityId || "").trim();
  if (!id || !mission) return false;
  const pocOrg = String(mission.missionPOCOrgId || "").trim();
  const postedBy = String(mission.postedBy || "").trim();
  const ownerEntity = String(
    mission.owner?.entityId || mission.ownerEntityId || "",
  ).trim();
  return pocOrg === id || postedBy === id || ownerEntity === id;
}

const FILTER_MISSION_SCOPE_OPTIONS = [
  { value: "mine", label: "My Missions" },
  { value: "all", label: "View All Missions" },
];

function MissionsPage() {
  const {
    user,
    tokenRole,
    tokenEntityType,
    tokenEntityId,
    tokenProfileId,
    profile,
    isGuest,
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingList, setLoadingList] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [missions, setMissions] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [filterCategory, setFilterCategory] = useState("");
  const [filterOriginState, setFilterOriginState] = useState("");
  const [filterDestinationState, setFilterDestinationState] = useState("");
  const [filterNeedType, setFilterNeedType] = useState("");
  const [filterFlightHours, setFilterFlightHours] = useState("");
  const [filterRating, setFilterRating] = useState("");
  const [filterVisibility, setFilterVisibility] = useState("");
  const [filterCoordinatorOrg, setFilterCoordinatorOrg] = useState("");
  const [missionScope, setMissionScope] = useState("all");
  const [listStatus, setListStatus] = useState("active");
  const [nonprofits, setNonprofits] = useState([]);
  const [sortBy, setSortBy] = useState("title");
  const [sortOrder, setSortOrder] = useState("asc");
  const [editingMissionId, setEditingMissionId] = useState(null);
  const [initialData, setInitialData] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [pendingDeleteMission, setPendingDeleteMission] = useState(null);
  const [deletingMission, setDeletingMission] = useState(false);
  const [pageToken, setPageToken] = useState(null);
  const [nextPageToken, setNextPageToken] = useState(null);
  const [tokenHistory, setTokenHistory] = useState([]);
  const [pageLoading, setPageLoading] = useState(false);
  const isViewOnlyUser = (tokenRole || "").toLowerCase() === "user" || isGuest;
  const currentProfileId = profile?.profileId || tokenProfileId;
  const canEditMission = (mission) =>
    canEditMissionForProfile(mission, {
      profileId: currentProfileId,
      isGuest,
      tokenRole,
      tokenEntityType,
    });
  const canDeleteMission = (mission) => canEditMission(mission);

  const isNonprofitContext = useMemo(
    () => !isGuest && isNonprofitEntityType(tokenEntityType),
    [isGuest, tokenEntityType],
  );
  const nonprofitEntityId = String(tokenEntityId || "").trim();
  const showNonprofitMissionScope =
    isNonprofitContext && Boolean(nonprofitEntityId);

  // Nonprofits land on My Missions by default; other roles see all.
  useEffect(() => {
    setMissionScope(showNonprofitMissionScope ? "mine" : "all");
  }, [showNonprofitMissionScope]);

  // Open edit form when navigating from View page with editMissionId
  useEffect(() => {
    const editId = location.state?.editMissionId;
    if (!editId) return;
    navigate(location.pathname, { replace: true, state: {} });
    handleEdit(editId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.editMissionId]);

  const fetchMissionsPage = useCallback(
    async (token = null, { isInitial = false } = {}) => {
      if (isGuest) {
        setMissions(guestMissions);
        setNextPageToken(null);
        setLoadingList(false);
        setPageLoading(false);
        return;
      }
      if (!user?.id_token) {
        setMissions([]);
        setNextPageToken(null);
        setLoadingList(false);
        setPageLoading(false);
        return;
      }

      if (isInitial) setLoadingList(true);
      else setPageLoading(true);

      try {
        const res = await getMissions(user.id_token, token);
        const list = normalizeMissionsList(res?.data);
        setMissions(Array.isArray(list) ? list : []);
        setNextPageToken(res?.nextToken ?? null);
        setPageToken(token);
      } catch {
        setMissions([]);
        setNextPageToken(null);
      } finally {
        setLoadingList(false);
        setPageLoading(false);
      }
    },
    [isGuest, user?.id_token],
  );

  const fetchMissionsList = useCallback(async () => {
    setTokenHistory([]);
    setPageToken(null);
    setNextPageToken(null);
    await fetchMissionsPage(null, { isInitial: true });
  }, [fetchMissionsPage]);

  const handleNextPage = async () => {
    if (!nextPageToken || pageLoading) return;
    setTokenHistory((prev) => [...prev, pageToken]);
    await fetchMissionsPage(nextPageToken);
  };

  const handlePreviousPage = async () => {
    if (tokenHistory.length === 0 || pageLoading) return;
    const prevHistory = [...tokenHistory];
    const previousToken = prevHistory.pop();
    setTokenHistory(prevHistory);
    await fetchMissionsPage(previousToken ?? null);
  };

  useEffect(() => {
    if (isGuest) {
      setMissions(guestMissions);
      setNonprofits(guestNonprofits);
      setNextPageToken(null);
      setLoadingList(false);
      return undefined;
    }
    if (!user?.id_token) {
      setLoadingList(false);
      setMissions([]);
      setNonprofits([]);
      setNextPageToken(null);
      return undefined;
    }

    fetchMissionsList();

    let cancelled = false;
    getNonProfits(user.id_token)
      .then((res) => {
        if (cancelled) return;
        const list = normalizeNonprofitsList(res?.data);
        setNonprofits(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setNonprofits([]);
      });
    return () => {
      cancelled = true;
    };
  }, [isGuest, user?.id_token, successMessage, refreshKey, fetchMissionsList]);

  useGlobalDataRefresh(
    ["mission", "missionid"],
    useCallback(() => {
      setRefreshKey((k) => k + 1);
    }, []),
  );

  const canGoPrevious = !isGuest && tokenHistory.length > 0;
  const canGoNext = !isGuest && Boolean(nextPageToken);

  const handleEdit = async (missionId) => {
    if (isViewOnlyUser) return;
    if (!missionId) return;
    setError(null);
    if (!user?.id_token) return;
    setLoading(true);
    try {
      const res = await getMissionById(missionId, user.id_token);
      let data = res?.data;
      if (data && typeof data === "string") data = JSON.parse(data);
      if (data && !data.missionId && !data.title) {
        const list = data?.MissionsList || data?.missions || data?.items;
        if (Array.isArray(list) && list.length > 0) data = list[0];
      }
      if (!canEditMission(data)) {
        setError("You can only edit missions you own.");
        return;
      }
      if (isInactiveMissionStatus(data)) {
        setError("This mission has been deleted.");
        return;
      }
      setInitialData(
        data ? applyMissionDerivedStatus(normalizeMissionRecord(data)) : null,
      );
      setEditingMissionId(missionId);
      setShowForm(true);
    } catch (err) {
      setError(err.message || "Failed to load mission.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (
    missionData,
    { pendingFiles = [], pendingBannerFile = null, onUploadProgress } = {},
  ) => {
    if (isViewOnlyUser) return;
    if (!user?.id_token) return;
    if (editingMissionId && !canEditMission(initialData)) {
      setError("You can only edit missions you own.");
      throw new Error("You can only edit missions you own.");
    }
    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const result = await saveMissionWithDocuments(
        missionData,
        user.id_token,
        editingMissionId || undefined,
        {
          pendingFiles,
          pendingBannerFile,
          onFileProgress: (name, percent) => onUploadProgress?.(name, percent),
        },
      );
      setSuccessMessage(result.message || "Mission saved successfully.");

      const savedMissionId =
        result.missionId ?? result.data?.missionId ?? editingMissionId ?? null;

      setShowForm(false);
      setEditingMissionId(null);
      setInitialData(null);

      if (savedMissionId) {
        navigate(`/dashboard/missions/${savedMissionId}`);
      } else {
        setRefreshKey((k) => k + 1);
      }
    } catch (err) {
      const message = err.message || "Failed to save mission.";
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingMissionId(null);
    setInitialData(null);
    setError(null);
  };

  const handleDeleteMission = async () => {
    const pending = pendingDeleteMission;
    if (!pending?.id || !user?.id_token || deletingMission || isGuest) return;

    setDeletingMission(true);
    setError(null);
    try {
      const result = await getMissionById(pending.id, user.id_token);
      let data = result?.data;
      if (data && typeof data === "string") data = JSON.parse(data);
      if (data && !data.missionId && !data.title) {
        const list = data?.MissionsList || data?.missions || data?.items;
        if (Array.isArray(list) && list.length > 0) data = list[0];
      }
      const record = data
        ? applyMissionDerivedStatus(normalizeMissionRecord(data))
        : null;

      if (!canDeleteMission(record)) {
        throw new Error("Only the mission owner can delete this mission.");
      }
      if (isInactiveMissionStatus(record)) {
        setMissions((current) =>
          current.filter(
            (m) => String(m.missionId || m.id) !== String(pending.id),
          ),
        );
        setPendingDeleteMission(null);
        return;
      }

      const saveResult = await saveMission(
        { status: "Inactive" },
        user.id_token,
        pending.id,
      );
      if (!saveResult.success) {
        throw new Error(saveResult.message || "Failed to delete mission.");
      }

      setMissions((current) =>
        current.filter(
          (m) => String(m.missionId || m.id) !== String(pending.id),
        ),
      );
      setPendingDeleteMission(null);
      window.dispatchEvent(
        new CustomEvent("globalNotification", {
          detail: {
            type: "success",
            title: "Success",
            message: "Mission deleted successfully.",
          },
        }),
      );
    } catch (err) {
      const message = err.message || "Failed to delete mission.";
      setError(message);
      window.dispatchEvent(
        new CustomEvent("globalNotification", {
          detail: {
            type: "error",
            title: "Error",
            message,
          },
        }),
      );
    } finally {
      setDeletingMission(false);
    }
  };

  const handleAddNew = () => {
    if (isViewOnlyUser) return;
    setEditingMissionId(null);
    setInitialData(null);
    setError(null);
    setSuccessMessage(null);
    setShowForm(true);
  };

  const hasActiveFilters =
    !!filterCategory ||
    !!filterOriginState ||
    !!filterDestinationState ||
    !!filterNeedType ||
    !!filterFlightHours ||
    !!filterRating ||
    !!filterVisibility ||
    !!filterCoordinatorOrg;

  const scopedMissions = missions.filter((m) => {
    if (isInactiveMissionStatus(m)) return false;
    if (isDraftMissionStatus(m) && !isMissionOwner(m, currentProfileId)) {
      return false;
    }
    if (
      showNonprofitMissionScope &&
      missionScope === "mine" &&
      !isNonprofitMyMission(m, {
        entityId: nonprofitEntityId,
        profileId: currentProfileId,
      })
    ) {
      return false;
    }
    return true;
  });

  const allMissionsCount = scopedMissions.filter(
    (m) => !isDraftMissionStatus(m),
  ).length;
  const draftMissionsCount = scopedMissions.filter(isDraftMissionStatus).length;

  const visibleMissions = scopedMissions.filter((m) =>
    listStatus === "draft" ? isDraftMissionStatus(m) : !isDraftMissionStatus(m),
  );

  const uniqueOriginStates = [
    ...new Set(
      visibleMissions
        .map((m) =>
          parseStateFromMissionLocation(m.departure, m.departureState),
        )
        .filter((s) => s),
    ),
  ].sort();
  const uniqueDestinationStates = [
    ...new Set(
      visibleMissions
        .map((m) => parseStateFromMissionLocation(m.arrival, m.arrivalState))
        .filter((s) => s),
    ),
  ].sort();
  const uniqueCoordinatorOrgs = [
    ...new Map(
      visibleMissions
        .map((m) => String(m.missionPOCOrgId || "").trim())
        .filter(Boolean)
        .map((orgId) => {
          const np = nonprofits.find((o) => getNonprofitId(o) === orgId);
          return [
            orgId,
            {
              value: orgId,
              label: np ? getNonprofitLabel(np) : orgId,
            },
          ];
        }),
    ).values(),
  ].sort((a, b) => a.label.localeCompare(b.label));

  const filteredMissions = visibleMissions.filter((m) => {
    const pilotStatus = (m.pilotStatus || "").toLowerCase();
    const cargoStatus = (m.cargoStatus || "").toLowerCase();
    const needsPilot =
      pilotStatus.includes("needed") || pilotStatus.includes("pending");
    const needsCargo =
      cargoStatus.includes("needed") || cargoStatus.includes("pending");

    if (filterCategory) {
      const known = [
        "medical",
        "animal rescue",
        "disaster relief",
        "veteran support",
        "conservation",
        "aviation experience",
      ];
      const cat = (m.missionCategory || "").toLowerCase();
      const match =
        filterCategory === "other"
          ? cat && !known.includes(cat)
          : cat === filterCategory.toLowerCase();
      if (!match) return false;
    }
    if (filterOriginState) {
      const originState = parseStateFromMissionLocation(
        m.departure,
        m.departureState,
      );
      if ((originState || "").toLowerCase() !== filterOriginState.toLowerCase())
        return false;
    }
    if (filterDestinationState) {
      const destState = parseStateFromMissionLocation(
        m.arrival,
        m.arrivalState,
      );
      if (
        (destState || "").toLowerCase() !== filterDestinationState.toLowerCase()
      )
        return false;
    }
    if (filterCoordinatorOrg) {
      if (String(m.missionPOCOrgId || "").trim() !== filterCoordinatorOrg)
        return false;
    }
    if (filterVisibility) {
      const type = String(m.type || "").trim();
      const normalizedType = type === "Private" ? "Internal" : type;
      if (normalizedType !== filterVisibility) return false;
    }
    if (filterNeedType === "pilot" && !needsPilot) return false;
    if (filterNeedType === "cargo" && !needsCargo) return false;
    if (filterNeedType === "both" && !(needsPilot && needsCargo)) return false;
    if (filterFlightHours) {
      const hours = String(
        m.pilotRequirements?.flightHourMinimums ?? m.minHours ?? "",
      ).trim();
      const normalizedHours = hours === "" ? "None" : hours;
      if (normalizedHours !== filterFlightHours) return false;
    }
    if (filterRating) {
      const required = String(
        m.pilotRequirements?.[filterRating] ?? "",
      )
        .trim()
        .toLowerCase();
      if (required !== "yes") return false;
    }
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      const title = (m.title || "").toLowerCase();
      const description = (m.description || "").toLowerCase();
      const departure = formatMissionEndpoint(
        m.departure,
        m.departureCity,
        m.departureState,
      ).toLowerCase();
      const arrival = formatMissionEndpoint(
        m.arrival,
        m.arrivalCity,
        m.arrivalState,
      ).toLowerCase();
      const status = (m.status || "").toLowerCase();
      const category = (m.missionCategory || "").toLowerCase();
      const matches =
        title.includes(q) ||
        description.includes(q) ||
        departure.includes(q) ||
        arrival.includes(q) ||
        status.includes(q) ||
        category.includes(q) ||
        getMissionVisibility(m).toLowerCase().includes(q);
      if (!matches) return false;
    }
    return true;
  });

  const getSortValue = (m, key) => {
    switch (key) {
      case "title":
        return (m.title || "").toLowerCase();
      case "route":
        return [
          formatMissionEndpoint(m.departure, m.departureCity, m.departureState),
          formatMissionEndpoint(m.arrival, m.arrivalCity, m.arrivalState),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
      case "visibility":
        return getMissionVisibility(m).toLowerCase();
    }
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder((o) => (o === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
  };

  const sortedMissions = [...filteredMissions].sort((a, b) => {
    const va = getSortValue(a, sortBy);
    const vb = getSortValue(b, sortBy);
    const cmp = va.localeCompare(vb, undefined, { numeric: true });
    return sortOrder === "asc" ? cmp : -cmp;
  });

  const missionColumns = [
    { name: "title", align: "left", sortable: true },
    { name: "visibility", align: "left", sortable: true },
    { name: "route", align: "left", sortable: true },
    { name: "actions", align: "center" },
  ];

  const missionRows = sortedMissions.map((mission, idx) => {
    const missionId = mission.missionId || mission.id || idx;
    const route = [
      formatMissionEndpoint(
        mission.departure,
        mission.departureCity,
        mission.departureState,
      ),
      formatMissionEndpoint(
        mission.arrival,
        mission.arrivalCity,
        mission.arrivalState,
      ),
    ]
      .filter(Boolean)
      .join(" → ");
    const isDraftMission = isDraftMissionStatus(mission);
    const visibility = getMissionVisibility(mission);
    const isInternalMission = visibility === "Internal";
    return {
      title: (
        <SoftBox display="flex" flexDirection="column">
          <SoftBox display="flex" alignItems="center" gap={1}>
            <SoftTypography variant="button" fontWeight="medium">
              {mission.title || "Untitled mission"}
            </SoftTypography>
            {isDraftMission && (
              <SoftBadge
                variant="contained"
                color="secondary"
                size="xs"
                badgeContent="Draft"
                container
              />
            )}
          </SoftBox>
          {mission.description && (
            <SoftTypography
              variant="caption"
              color="secondary"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: 300,
              }}
            >
              {mission.description}
            </SoftTypography>
          )}
        </SoftBox>
      ),
      visibility: (
        <SoftBadge
          variant="contained"
          color={isInternalMission ? "warning" : "success"}
          size="xs"
          badgeContent={visibility}
          container
        />
      ),
      route: (
        <SoftTypography variant="caption" color="secondary">
          {route || "—"}
        </SoftTypography>
      ),
      actions: (
        <SoftBox
          display="flex"
          alignItems="center"
          justifyContent="center"
          gap={1}
          sx={{ minHeight: 36 }}
        >
          <SoftButton
            variant="text"
            color="info"
            size="small"
            onClick={() => navigate(`/dashboard/missions/${missionId}`)}
            sx={{ minWidth: 82, justifyContent: "center" }}
          >
            <Icon>visibility</Icon>&nbsp;View
          </SoftButton>
          {canEditMission(mission) && (
            <SoftButton
              variant="text"
              color="dark"
              size="small"
              onClick={() => handleEdit(missionId)}
              sx={{ minWidth: 72, justifyContent: "center" }}
            >
              <Icon>edit</Icon>&nbsp;Edit
            </SoftButton>
          )}
          {!isViewOnlyUser && !canEditMission(mission) && (
            <SoftBox sx={{ minWidth: 72 }} />
          )}
          {canDeleteMission(mission) && (
            <SoftButton
              variant="text"
              color="error"
              size="small"
              onClick={() =>
                setPendingDeleteMission({
                  id: String(missionId),
                  title: mission.title || "Untitled mission",
                })
              }
              sx={{ minWidth: 148, justifyContent: "center" }}
            >
              <Icon>delete_outline</Icon>&nbsp;Delete
            </SoftButton>
          )}
          {!isViewOnlyUser && !canDeleteMission(mission) && (
            <SoftBox sx={{ minWidth: 148 }} />
          )}
        </SoftBox>
      ),
    };
  });

  return (
    <SoftBox py={3}>
      <SoftBox
        mb={3}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={2}
      >
        <SoftBox>
          <SoftTypography variant="h4" fontWeight="bold" gutterBottom>
            Missions
          </SoftTypography>
          <SoftTypography variant="body2" color="text">
            {isGuest
              ? "Sample missions (read-only)."
              : "Create and manage missions (relief, cargo, passengers)."}
          </SoftTypography>
        </SoftBox>
        {!showForm && !isViewOnlyUser && (
          <SoftButton variant="gradient" color="info" onClick={handleAddNew}>
            <Icon sx={{ mr: 1 }}>add</Icon>
            Add Mission
          </SoftButton>
        )}
      </SoftBox>

      {successMessage && (
        <SoftBox mb={2}>
          <Alert severity="success" onClose={() => setSuccessMessage(null)}>
            {successMessage}
          </Alert>
        </SoftBox>
      )}

      {error && (
        <SoftBox mb={2}>
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        </SoftBox>
      )}

      {showForm ? (
        <Card sx={{ overflow: "visible" }}>
          <SoftBox p={3} sx={{ overflow: "visible" }}>
            <SoftTypography variant="h6" fontWeight="bold" gutterBottom>
              {editingMissionId ? "Edit Mission" : "New Mission"}
            </SoftTypography>
            <MissionForm
              key={editingMissionId ?? "new-mission"}
              onSubmit={handleSubmit}
              onCancel={handleCancel}
              initialData={initialData}
              loading={loading}
              idToken={user?.id_token}
            />
          </SoftBox>
        </Card>
      ) : (
        <Card>
          {!loadingList && (
            <Tabs
              value={listStatus === "draft" ? 1 : 0}
              onChange={(_e, value) =>
                setListStatus(value === 1 ? "draft" : "active")
              }
              sx={{ px: 2, pt: 2 }}
            >
              <Tab label={`All Missions (${allMissionsCount})`} />
              <Tab label={`Drafts (${draftMissionsCount})`} />
            </Tabs>
          )}
          <SoftBox p={3}>
            {loadingList ? (
              <SoftBox display="flex" alignItems="center" gap={1} py={2}>
                <CircularProgress size={24} />
                <SoftTypography variant="body2">
                  Loading missions...
                </SoftTypography>
              </SoftBox>
            ) : (
              <>
                {
                  <SoftBox
                    mb={2}
                    display="flex"
                    justifyContent="flex-end"
                    alignItems="center"
                    gap={1}
                    flexWrap="wrap"
                  >
                    {showNonprofitMissionScope ? (
                      <SoftBox
                        display="flex"
                        alignItems="center"
                        gap={1}
                        mr="auto"
                        flexWrap="wrap"
                      >
                        <SoftButton
                          size="small"
                          variant={
                            missionScope === "mine" ? "gradient" : "outlined"
                          }
                          color="info"
                          onClick={() => setMissionScope("mine")}
                        >
                          My Missions
                        </SoftButton>
                        <SoftButton
                          size="small"
                          variant={
                            missionScope === "all" ? "gradient" : "outlined"
                          }
                          color="info"
                          onClick={() => setMissionScope("all")}
                        >
                          View All Missions
                        </SoftButton>
                      </SoftBox>
                    ) : (
                      <SoftBox sx={{ mr: "auto" }} />
                    )}
                    {missions.length > 0 ? (
                      <>
                        <SoftBox
                          sx={{
                            width: 360,
                            maxWidth: "100%",
                            "&:focus-within": {
                              "& .MuiInputBase-root": {
                                border: "2px solid",
                                borderColor: "info.main",
                              },
                            },
                            "& .MuiInputBase-root.Mui-focused": {
                              boxShadow: "none",
                              borderColor: "transparent !important",
                            },
                          }}
                        >
                          <SoftInput
                            placeholder="Search by title, description, route..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            icon={{ component: "search", direction: "left" }}
                          />
                        </SoftBox>
                        <IconButton
                          onClick={(e) => setFilterAnchorEl(e.currentTarget)}
                          color={hasActiveFilters ? "info" : "default"}
                          title="Filter"
                          sx={{
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 1,
                            "&:hover": { backgroundColor: "action.hover" },
                          }}
                        >
                          <Icon>filter_list</Icon>
                        </IconButton>
                      </>
                    ) : null}
                    <Popover
                      open={!!filterAnchorEl}
                      anchorEl={filterAnchorEl}
                      onClose={() => setFilterAnchorEl(null)}
                      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                      transformOrigin={{ vertical: "top", horizontal: "right" }}
                      PaperProps={{
                        sx: (theme) => ({
                          backgroundColor: theme.palette.background.paper,
                          backgroundImage: "none",
                          borderRadius: "1rem",
                          border: `1px solid ${theme.palette.divider}`,
                          boxShadow: "0 20px 40px rgba(15, 34, 58, 0.12)",
                          minWidth: 240,
                        }),
                      }}
                    >
                      <SoftBox p={2.5} minWidth={240}>
                        <SoftBox
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          mb={1.5}
                        >
                          <SoftTypography
                            variant="button"
                            fontWeight="bold"
                            color="dark"
                          >
                            Filter by
                          </SoftTypography>
                          <SoftButton
                            size="small"
                            variant="text"
                            color="secondary"
                            onClick={() => {
                              setFilterCategory("");
                              setFilterOriginState("");
                              setFilterDestinationState("");
                              setFilterNeedType("");
                              setFilterFlightHours("");
                              setFilterRating("");
                              setFilterVisibility("");
                              setFilterCoordinatorOrg("");
                            }}
                            disabled={!hasActiveFilters}
                            sx={{
                              visibility: hasActiveFilters
                                ? "visible"
                                : "hidden",
                              minWidth: 80,
                            }}
                          >
                            Clear filters
                          </SoftButton>
                        </SoftBox>
                        {showNonprofitMissionScope ? (
                          <SoftBox mb={1.5}>
                            <SoftTypography
                              variant="caption"
                              fontWeight="bold"
                              display="block"
                              mb={0.5}
                            >
                              Mission list
                            </SoftTypography>
                            <SoftSelect
                              placeholder="My Missions"
                              options={FILTER_MISSION_SCOPE_OPTIONS}
                              value={
                                FILTER_MISSION_SCOPE_OPTIONS.find(
                                  (o) => o.value === missionScope,
                                ) || FILTER_MISSION_SCOPE_OPTIONS[0]
                              }
                              onChange={(opt) =>
                                setMissionScope(
                                  opt?.value === "all" ? "all" : "mine",
                                )
                              }
                              menuPortalTarget={document.body}
                            />
                          </SoftBox>
                        ) : null}
                        <SoftBox mb={1}>
                          <SoftTypography
                            variant="caption"
                            fontWeight="bold"
                            display="block"
                            mb={0.5}
                          >
                            Needs
                          </SoftTypography>
                          <SoftSelect
                            placeholder="All"
                            options={FILTER_NEED_OPTIONS}
                            value={
                              FILTER_NEED_OPTIONS.find(
                                (o) => o.value === filterNeedType,
                              ) || null
                            }
                            onChange={(opt) =>
                              setFilterNeedType(opt?.value ?? "")
                            }
                            isClearable
                            menuPortalTarget={document.body}
                          />
                        </SoftBox>
                        <SoftBox mb={1}>
                          <SoftTypography
                            variant="caption"
                            fontWeight="bold"
                            display="block"
                            mb={0.5}
                          >
                            Flight Hour Minimums
                          </SoftTypography>
                          <SoftSelect
                            placeholder="All"
                            options={FILTER_FLIGHT_HOUR_OPTIONS}
                            value={
                              FILTER_FLIGHT_HOUR_OPTIONS.find(
                                (o) => o.value === filterFlightHours,
                              ) || null
                            }
                            onChange={(opt) =>
                              setFilterFlightHours(opt?.value ?? "")
                            }
                            isClearable
                            menuPortalTarget={document.body}
                          />
                        </SoftBox>
                        <SoftBox mb={1}>
                          <SoftTypography
                            variant="caption"
                            fontWeight="bold"
                            display="block"
                            mb={0.5}
                          >
                            Ratings
                          </SoftTypography>
                          <SoftSelect
                            placeholder="All"
                            options={FILTER_RATING_OPTIONS}
                            value={
                              FILTER_RATING_OPTIONS.find(
                                (o) => o.value === filterRating,
                              ) || null
                            }
                            onChange={(opt) =>
                              setFilterRating(opt?.value ?? "")
                            }
                            isClearable
                            menuPortalTarget={document.body}
                          />
                        </SoftBox>
                        <SoftBox mb={1}>
                          <SoftTypography
                            variant="caption"
                            fontWeight="bold"
                            display="block"
                            mb={0.5}
                          >
                            Category
                          </SoftTypography>
                          <SoftSelect
                            placeholder="All categories"
                            options={FILTER_CATEGORY_OPTIONS}
                            value={
                              FILTER_CATEGORY_OPTIONS.find(
                                (o) => o.value === filterCategory,
                              ) || null
                            }
                            onChange={(opt) =>
                              setFilterCategory(opt?.value ?? "")
                            }
                            isClearable
                            menuPortalTarget={document.body}
                          />
                        </SoftBox>
                        <SoftBox mb={1}>
                          <SoftTypography
                            variant="caption"
                            fontWeight="bold"
                            display="block"
                            mb={0.5}
                          >
                            Public Visibility
                          </SoftTypography>
                          <SoftSelect
                            placeholder="All"
                            options={FILTER_VISIBILITY_OPTIONS}
                            value={
                              FILTER_VISIBILITY_OPTIONS.find(
                                (o) => o.value === filterVisibility,
                              ) || null
                            }
                            onChange={(opt) =>
                              setFilterVisibility(opt?.value ?? "")
                            }
                            isClearable
                            menuPortalTarget={document.body}
                          />
                        </SoftBox>
                        <SoftBox mb={1}>
                          <SoftTypography
                            variant="caption"
                            fontWeight="bold"
                            display="block"
                            mb={0.5}
                          >
                            Origin state
                          </SoftTypography>
                          <SoftSelect
                            placeholder="All"
                            options={[
                              { value: "", label: "All" },
                              ...uniqueOriginStates.map((s) => ({
                                value: s,
                                label: s,
                              })),
                            ]}
                            value={
                              filterOriginState
                                ? {
                                    value: filterOriginState,
                                    label: filterOriginState,
                                  }
                                : null
                            }
                            onChange={(opt) =>
                              setFilterOriginState(opt?.value ?? "")
                            }
                            isClearable
                            menuPortalTarget={document.body}
                          />
                        </SoftBox>
                        <SoftBox mb={1}>
                          <SoftTypography
                            variant="caption"
                            fontWeight="bold"
                            display="block"
                            mb={0.5}
                          >
                            Destination state
                          </SoftTypography>
                          <SoftSelect
                            placeholder="All"
                            options={[
                              { value: "", label: "All" },
                              ...uniqueDestinationStates.map((s) => ({
                                value: s,
                                label: s,
                              })),
                            ]}
                            value={
                              filterDestinationState
                                ? {
                                    value: filterDestinationState,
                                    label: filterDestinationState,
                                  }
                                : null
                            }
                            onChange={(opt) =>
                              setFilterDestinationState(opt?.value ?? "")
                            }
                            isClearable
                            menuPortalTarget={document.body}
                          />
                        </SoftBox>
                        <SoftBox mb={1}>
                          <SoftTypography
                            variant="caption"
                            fontWeight="bold"
                            display="block"
                            mb={0.5}
                          >
                            Coordinator Organization
                          </SoftTypography>
                          <SoftSelect
                            placeholder="All organizations"
                            options={[
                              { value: "", label: "All organizations" },
                              ...uniqueCoordinatorOrgs,
                            ]}
                            value={
                              uniqueCoordinatorOrgs.find(
                                (o) => o.value === filterCoordinatorOrg,
                              ) || null
                            }
                            onChange={(opt) =>
                              setFilterCoordinatorOrg(opt?.value ?? "")
                            }
                            isClearable
                            menuPortalTarget={document.body}
                          />
                        </SoftBox>
                      </SoftBox>
                    </Popover>
                  </SoftBox>
                }
                {visibleMissions.length === 0 ? (
                  <SoftTypography variant="body2" color="text">
                    {listStatus === "draft"
                      ? 'No draft missions. Open the "All Missions" tab to see published missions, or use "Add Mission" and save as draft.'
                      : showNonprofitMissionScope && missionScope === "mine"
                      ? 'No missions for your organization yet. Switch to "View All Missions" or use "Add Mission" to create one.'
                      : isViewOnlyUser
                      ? "No missions available to view."
                      : 'No missions yet. Use "Add Mission" to create a new mission with routing, cargo, passengers, and POC details.'}
                  </SoftTypography>
                ) : filteredMissions.length === 0 ? (
                  <SoftBox py={4} textAlign="center">
                    <SoftTypography variant="body2" color="text">
                      No missions match your search or filters. Try different
                      criteria.
                    </SoftTypography>
                  </SoftBox>
                ) : (
                  <>
                    <Table
                      columns={missionColumns}
                      rows={missionRows}
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={handleSort}
                    />
                    <SoftBox
                      mt={2}
                      display="flex"
                      justifyContent="space-between"
                      alignItems={{ xs: "flex-start", sm: "center" }}
                      flexDirection={{ xs: "column", sm: "row" }}
                      gap={2}
                    >
                      <SoftTypography
                        variant="button"
                        color="secondary"
                        fontWeight="regular"
                      >
                        Showing {filteredMissions.length}{" "}
                        {filteredMissions.length === 1 ? "mission" : "missions"}
                        {searchQuery.trim() || hasActiveFilters
                          ? " (filtered on this page)"
                          : ""}
                        {canGoNext || canGoPrevious ? " · more available" : ""}
                      </SoftTypography>
                      {canGoPrevious || canGoNext ? (
                        <SoftPagination variant="gradient" color="info">
                          <SoftPagination
                            item
                            onClick={handlePreviousPage}
                            disabled={!canGoPrevious || pageLoading}
                          >
                            <Icon sx={{ fontWeight: "bold" }}>
                              chevron_left
                            </Icon>
                          </SoftPagination>
                          <SoftTypography
                            variant="button"
                            fontWeight="medium"
                            mx={1}
                            sx={{ alignSelf: "center" }}
                          >
                            {pageLoading ? "Loading…" : "Prev / Next"}
                          </SoftTypography>
                          <SoftPagination
                            item
                            onClick={handleNextPage}
                            disabled={!canGoNext || pageLoading}
                          >
                            <Icon sx={{ fontWeight: "bold" }}>
                              chevron_right
                            </Icon>
                          </SoftPagination>
                        </SoftPagination>
                      ) : null}
                    </SoftBox>
                  </>
                )}
              </>
            )}
          </SoftBox>
        </Card>
      )}
      <Dialog
        open={!!pendingDeleteMission}
        onClose={() => !deletingMission && setPendingDeleteMission(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete mission?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete "{pendingDeleteMission?.title}"? This will mark the mission
            as inactive and remove it from the list.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <SoftButton
            variant="text"
            color="secondary"
            onClick={() => setPendingDeleteMission(null)}
            disabled={deletingMission}
          >
            Cancel
          </SoftButton>
          <SoftButton
            variant="gradient"
            color="error"
            onClick={handleDeleteMission}
            disabled={deletingMission}
          >
            {deletingMission ? "Deleting…" : "Delete"}
          </SoftButton>
        </DialogActions>
      </Dialog>
    </SoftBox>
  );
}

export default MissionsPage;
